import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { ItemActionType } from './SetMarketplaceAction';
import { useEffect, useRef, useState } from 'react';
import P2GContentDropDown from '../../P2GContentDropDown';
import {
  P2GBox,
  P2GCheckBoxLegacy,
  P2GGrid,
  P2GLink,
  P2GProgress,
  P2GText,
  SvgConfirmCircle,
  useP2GInView
} from '@parcel2go/core-ui';
import AlertWithIcon from '../../AlertWithIcon';
import { HasExternalTrackingNumber } from '~/apps/smartsend/lib/helpers/SmartSendSummaryGenerator';
import { AsyncLinks } from '../actions/book/AsyncLinks';
import { LinkModel } from '../actions/book/LinkModel';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { createAuthFailureCircuitBreaker } from '~/lib/api/auth-failure-circuit-breaker';
import { MarketplaceSupportsMarkingAsDispatched } from '~/apps/smartsend/lib/helpers/MarketplaceSupportsMarkingAsDispatched';

interface props {
  item: SmartSendItemApiModel;
  togglePackaged: () => void;
  markAsDispatched: (isRetry?: boolean) => void;
  size: 'small' | 'normal';
  packaged: boolean;
  actions: ItemActionType[];
}

function FallbackTrackingNumberContent(
  props: props & {
    marketplaceDispatchStatus: string;
    isAutomatic: boolean;
    visibilityRef: (node?: Element | null) => void;
  }
) {
  const [tooLong, setTooLong] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTooLong(true);
    }, 5_000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <P2GContentDropDown
      control={
        <P2GLink>
          <span ref={props.visibilityRef}>Notify Marketplace</span>
        </P2GLink>
      }
    >
      <div style={{ width: '350px' }}>
        <AlertWithIcon loader={props.isAutomatic ? <SvgConfirmCircle /> : <P2GProgress />}>
          <P2GText component="p" variant="body2">
            <strong>{props.marketplaceDispatchStatus}</strong>
          </P2GText>
          {props.isAutomatic && (
            <P2GBox mt={1}>
              <P2GText variant="body2">
                Your item will automatically update tracking information as soon as it becomes available.
              </P2GText>
            </P2GBox>
          )}
          {tooLong && props.item.BookingDetails?.Reference && (
            <P2GBox mt={1}>
              <P2GText component="p" variant="body2">
                <P2GLink onClick={(_e) => props.markAsDispatched(false)}>
                  Update using <strong>{props.item.BookingDetails?.Reference}</strong>
                </P2GLink>
              </P2GText>
            </P2GBox>
          )}
        </AlertWithIcon>
      </div>
    </P2GContentDropDown>
  );
}

export default function MarketplaceActionsAwaitingDispatch(props: props) {
  const { getItemMarketplaceDispatchStatus } = useSmartSendApi();

  // The polling effect below only re-runs when `inView` changes, so it would
  // capture a stale access token. Keep a ref at the latest fetcher so each poll
  // uses the current (renewed) token rather than replaying an expired one.
  const getItemStatusRef = useRef(getItemMarketplaceDispatchStatus);
  getItemStatusRef.current = getItemMarketplaceDispatchStatus;

  // Holds the polling timer so the circuit breaker can stop it from inside the loop.
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const [marketplaceDispatchStatus, setMarketplaceDispatchStatus] = useState<string>(props.item.MarketplaceStatus);
  const [isAutomatic, setIsAutomatcic] = useState<boolean>(false);

  const [ref, inView] = useP2GInView({
    triggerOnce: false,
    rootMargin: '20px 0px'
  });

  // Refresh from API every 5 seconds while in view. Only schedule the timer when the row
  // is actually visible and supports marketplace dispatch, so off-screen rows don't run
  // idle 5s timers. The circuit breaker only needs to run while we're polling the API,
  // which is exactly when the row is in view.
  useEffect(() => {
    if (!inView || !MarketplaceSupportsMarkingAsDispatched(props.item)) {
      return;
    }
    const breaker = createAuthFailureCircuitBreaker();
    const loadItemMPdispatchStatus = async () => {
      const apiResponse = await getItemStatusRef.current(props.item.Id);
      // Stop polling after repeated auth failures (e.g. signed out in another tab).
      if (breaker.record(apiResponse)) {
        clearInterval(pollTimerRef.current);
        return;
      }
      if (apiResponse.success && apiResponse.result) {
        setMarketplaceDispatchStatus(apiResponse.result.OverallDispatchStatus);
        if (apiResponse.result.MarketplaceOrderlineDispatches?.length > 0) {
          setIsAutomatcic(apiResponse.result.MarketplaceOrderlineDispatches.every((x) => x.IsAutomaticDispatch));
        }
      }
    };
    // If we've set to PendingRetry in the Reducer, we don't want the API to override it, until the next interval.
    // This is to give the retry time to start.
    if (props.item.MarketplaceStatus !== 'PendingRetry') {
      loadItemMPdispatchStatus();
    }
    pollTimerRef.current = setInterval(loadItemMPdispatchStatus, 5 * 1000);
    return () => clearInterval(pollTimerRef.current);
  }, [inView]);

  if (props.actions.length === 0) {
    return null;
  }
  return (
    <>
      <span ref={ref}></span>
      {props.size === 'normal' ? (
        <AsyncLinks
          tag=""
          links={[
            props.actions.includes('MARK-AS-DISPATCHED') &&
              GetMarkAsDispatchLinkModelContent(props, marketplaceDispatchStatus, isAutomatic, ref),
            props.actions.includes('MARK-AS-PACKAGED') && {
              Type: 'MARKPACKAGED_ONCLICK',
              Label: 'Mark as Packaged',
              Href: null,
              OnClick: props.togglePackaged,
              Checked: props.packaged
            }
          ]}
          variant="list"
          match={null}
          title={null}
        />
      ) : (
        <>
          {props.actions.includes('MARK-AS-DISPATCHED') && (
            <P2GGrid item flexShrink={1}>
              {GetMarkAsDispatchCheckBoxContent(props, marketplaceDispatchStatus, isAutomatic, ref)}
            </P2GGrid>
          )}
          {props.actions.includes('MARK-AS-PACKAGED') && (
            <P2GGrid item flexShrink={1}>
              <P2GCheckBoxLegacy
                checked={props.packaged}
                id="Packaged"
                onChange={props.togglePackaged}
                label={<P2GText variant="body2">Packaged</P2GText>}
              />
            </P2GGrid>
          )}
        </>
      )}
    </>
  );
}

function GetMarkAsDispatchLinkModelContent(
  props: props,
  marketplaceDispatchStatus: string,
  isAutomatic: boolean,
  ref: (node?: Element) => void
): LinkModel {
  switch (marketplaceDispatchStatus) {
    // OrderComplete
    case 'AwaitingTrackingDetails':
      return {
        Type: 'MARKDISPATCHED_ONCLICK',
        Label: 'Notify Marketplace',
        Href: null,
        Control: (
          <FallbackTrackingNumberContent
            {...props}
            marketplaceDispatchStatus={marketplaceDispatchStatus}
            visibilityRef={ref}
            isAutomatic={isAutomatic}
          />
        )
      };

    // OrderBooked
    case 'AwaitingDispatch':
      return {
        Type: 'MARKDISPATCHED_ONCLICK',
        Label: 'Notify Marketplace',
        Href: null,
        OnClick: null,
        Checked: null,
        Control: (
          <P2GCheckBoxLegacy
            checked={false}
            id={'MARKDISPATCHED_ONCLICK'}
            onChange={props.markAsDispatched}
            label={<span ref={ref}>Notify Marketplace</span>}
            disabled={false}
          />
        )
      };

    case 'InProgress':
    case 'PendingRetry':
      return {
        Type: 'MARKDISPATCHED_ONCLICK',
        Label: 'Notify Marketplace',
        Href: null,
        Control: (
          <P2GText variant="body2">
            <span ref={ref}>Updating {props.item.Source.Name}...</span>
          </P2GText>
        )
      };

    case 'Dispatched':
      return {
        Type: 'MARKDISPATCHED_ONCLICK',
        Label: 'Notify Marketplace',
        Href: null,
        OnClick: null,
        Checked: null,
        Disabled: null,
        Control: (
          <P2GCheckBoxLegacy
            checked={true}
            id={'MARKDISPATCHED_ONCLICK'}
            onChange={() => {}}
            label={<span ref={ref}>Notify Marketplace</span>}
            disabled={true}
          />
        )
      };

    case 'Failed':
      return {
        Type: 'MARKDISPATCHED_RETRY_ONCLICK',
        Label: 'Retry Notify Marketplace',
        Href: null,
        OnClick: null,
        Checked: null,
        Control: (
          <P2GCheckBoxLegacy
            checked={false}
            disabled={false}
            id={'MARKDISPATCHED_RETRY_ONCLICK'}
            onChange={() => {
              props.markAsDispatched(true); // isRetry = true
            }}
            label={<span ref={ref}>Retry Notify Marketplace</span>}
          />
        )
      };

    default: {
      if (isAutomatic) {
        return {
          Type: 'MARKDISPATCHED_ONCLICK',
          Label: 'Notify Marketplace',
          Href: null,
          Control: (
            <P2GText variant="body2">
              <span ref={ref}>Updating {props.item.Source.Name}...</span>
            </P2GText>
          )
        };
      }

      return HasExternalTrackingNumber(props.item)
        ? {
            Type: 'MARKDISPATCHED_ONCLICK',
            Label: 'Notify Marketplace',
            Href: null,
            OnClick: props.markAsDispatched,
            Checked: false
          }
        : {
            Type: 'MARKDISPATCHED_ONCLICK',
            Label: 'Notify Marketplace',
            Href: null,
            Control: (
              <FallbackTrackingNumberContent
                {...props}
                marketplaceDispatchStatus={marketplaceDispatchStatus}
                visibilityRef={ref}
                isAutomatic={isAutomatic}
              />
            )
          };
    }
  }
}

function GetMarkAsDispatchCheckBoxContent(
  props: props,
  marketplaceDispatchStatus: string,
  isAutomatic: boolean,
  ref: (node?: Element) => void
): React.ReactNode {
  switch (marketplaceDispatchStatus) {
    case 'AwaitingTrackingDetails':
      return (
        <div ref={ref}>
          <FallbackTrackingNumberContent
            {...props}
            marketplaceDispatchStatus={marketplaceDispatchStatus}
            visibilityRef={ref}
            isAutomatic={isAutomatic}
          />
        </div>
      );

    case 'AwaitingDispatch':
      return (
        <P2GCheckBoxLegacy
          disabled={false}
          id="Dispatched"
          checked={false}
          onChange={props.markAsDispatched}
          label={
            <P2GText variant="body2">
              <span ref={ref}>Notify Marketplace</span>
            </P2GText>
          }
        />
      );

    case 'InProgress':
    case 'PendingRetry':
      return (
        <P2GText variant="body2">
          <span ref={ref}>Updating {props.item.MarketplaceOrderLines[0].Source.Name}...</span>
        </P2GText>
      );

    case 'Dispatched':
      return (
        <P2GCheckBoxLegacy
          checked={true}
          id="Dispatched"
          disabled={true}
          onChange={() => {}}
          label={
            <P2GText variant="body2">
              <span ref={ref}>Notify Marketplace</span>
            </P2GText>
          }
        />
      );

    case 'Failed':
      return (
        <P2GLink
          id="Dispatched"
          onClick={() => {
            props.markAsDispatched(true); // isRetry = true
          }}
        >
          <span ref={ref}>Retry Notify Marketplace</span>
        </P2GLink>
      );

    default: {
      if (isAutomatic) {
        return (
          <P2GText variant="body2">
            <span ref={ref}>Updating {props.item.MarketplaceOrderLines[0].Source.Name}...</span>
          </P2GText>
        );
      }

      return HasExternalTrackingNumber(props.item) ? (
        <P2GCheckBoxLegacy
          id="Dispatched"
          checked={false}
          onChange={props.markAsDispatched}
          label={
            <P2GText variant="body2">
              <span ref={ref}>Notify Marketplace</span>
            </P2GText>
          }
        />
      ) : (
        <FallbackTrackingNumberContent
          {...props}
          marketplaceDispatchStatus={marketplaceDispatchStatus}
          visibilityRef={ref}
          isAutomatic={isAutomatic}
        />
      );
    }
  }
}
